import React, {
	useCallback,
	useMemo,
} from 'react';
import {
	Col,
	Empty,
	Row,
	Space,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import LoadingIndicator from '@client/components/LoadingIndicator';
import manOnBoat from '@client/assets/images/man_on_boat.svg';
import Table from '@client/components/Table/Table';
import FormPopover from '@client/components/FormPopover';
import { useMiscHandlers } from '@client/screens/estimates/details/context/hooks/useMiscHandlers';
import Modal from '@client/components/Modal/Modal';
import CommencementLinking from '../CommencementLinking';
import PortCallModal from '../PortCallModal';
import Itinerary from './Itinerary';
import styles from './ItineraryTab.module.css';
import { useItinerary } from './ItineraryProvider';
import getFormFields, { NewPortCallFormValues } from './utils/newPortCallForm';
import {
	getPortTimezone,
	normalizePortDateTime,
} from './components/PortAwareDatePicker';

const ItineraryTab = () => {
	const {
		expandedEntry,
		onSelectEntry,
		itineraryLoading,
		itinerary,
		showCommencementLinking,
		setShowCommencementLinking,
		addItineraryEntry,
		setItineraryShouldUpdate,
		timeZoneFormat,
	} = useItinerary();

	const {
		portOptions,
	} = useMiscHandlers();

	const formFields = useMemo(
		() => getFormFields(portOptions, itinerary ?? [], timeZoneFormat),
		[itinerary, portOptions, timeZoneFormat],
	);

	const handleAddItineraryEntry = useCallback((values: NewPortCallFormValues) => {
		const selectedTimeZoneFormat = values.timeZoneFormat ?? timeZoneFormat;
		const timezone = getPortTimezone(values.portId);

		return addItineraryEntry({
			portId: values.portId,
			robs: values.robs,
			estimatedDepartureDate: normalizePortDateTime(
				values.estimatedDepartureDate,
				selectedTimeZoneFormat,
				timezone,
			) ?? undefined,
			actualDepartureDate: normalizePortDateTime(
				values.actualDepartureDate,
				selectedTimeZoneFormat,
				timezone,
			) ?? undefined,
		});
	}, [addItineraryEntry, timeZoneFormat]);

	return (
		<>
			{itinerary?.length === 0 ? (
				<Table
					className={styles.fakeTable}
					size="small"
					locale={{
						emptyText: (
							<Empty
								image={manOnBoat}
								className={styles.emptyMessage}
								description={(
									<Row gutter={[16, 16]} className={styles.paddingBottom}>
										<Col span={24}>
											The itinerary is currently empty.
											<br />
											To get started, add a the first port call.
										</Col>
										<Col span={24}>
											<FormPopover
												title="Add planned port call"
												popoverWidth={550}
												// @ts-ignore
												fields={formFields}
												initialValues={{ timeZoneFormat }}
												buttonText={(
													<Space>
														<FontAwesomeIcon icon={faPlus} />
														Add Planned Port Call
													</Space>
												)}
												disabled={false}
												onSubmit={handleAddItineraryEntry}
											/>
										</Col>
									</Row>
								)}
							/>
						),

					}}
					columns={[
						{
							title: '',
						},
						{
							title: 'Port',
						},
						{
							title: 'Arrival date',
						},
						{
							title: 'Departure date',
						},
						{
							title: 'Cargo',
						},
						{
							title: 'Activities',
						},
						{
							title: '',
						},
					]}
				/>
			) : (
				<>
					<Row gutter={[4, 4]}>
						<Col span={24}>
							{itineraryLoading && (
								<div className={styles.itinLoadingOverlay}>
									<LoadingIndicator />
								</div>
							)}
							<Itinerary />
						</Col>
					</Row>
					<Modal
						key={expandedEntry?.id}
						className={styles.modal}
						width="80%"
						footer={null}
						open={expandedEntry != null}
						onCancel={() => onSelectEntry(undefined)}
						styles={{ body: { padding: 0 } }}
					>
						<PortCallModal key={expandedEntry?.id} />
					</Modal>
				</>
			)}
			<Modal
				open={showCommencementLinking}
				footer={null}
				width={1200}
				destroyOnClose
				onCancel={() => {
					setItineraryShouldUpdate(true);
					setShowCommencementLinking(false);
				}}
			>
				<CommencementLinking />
			</Modal>
		</>
	);
};

export default ItineraryTab;
